import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import ParentSelector from "../components/ParentSelector.jsx";
import { toast } from "sonner";
import { ArrowLeft, Upload, Save, Trash2 } from "lucide-react";

const COLOR_OPTIONS = ["Хүрэн", "Халзан", "Шарга", "Хээр", "Бор", "Цагаан", "Хар", "Зээрд", "Цагаан халзан", "Бор халзан"];
const GENDER_OPTIONS = ["Азарга", "Гүү", "Үрээ", "Унага", "Даага", "Шүдлэн", "Хязаалан", "Соёолон"];

const empty = {
  name: "", birth_year: "", color: "", gender: "", microchip: "", brand_stamp: "",
  father_id: "", mother_id: "",
  owner_name: "", breeder_farm: "", registration_number: "", country: "",
  description: "", achievements: "",
  is_for_sale: false, sale_price: "", sale_contact: "",
};

const HorseForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(empty);
  const [fatherName, setFatherName] = useState("");
  const [motherName, setMotherName] = useState("");
  const [photoPaths, setPhotoPaths] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/horses/${id}`);
        setForm({
          name: data.name || "",
          birth_year: data.birth_year || "",
          color: data.color || "",
          gender: data.gender || "",
          microchip: data.microchip || "",
          brand_stamp: data.brand_stamp || "",
          father_id: data.father_id || "",
          mother_id: data.mother_id || "",
          owner_name: data.owner_name || "",
          breeder_farm: data.breeder_farm || "",
          registration_number: data.registration_number || "",
          country: data.country || "",
          description: data.description || "",
          achievements: data.achievements || "",
          is_for_sale: !!data.is_for_sale,
          sale_price: data.sale_price || "",
          sale_contact: data.sale_contact || "",
        });
        setFatherName(data.father?.name || "");
        setMotherName(data.mother?.name || "");
        const paths = (data.photo_paths && data.photo_paths.length > 0)
          ? data.photo_paths
          : (data.photo_path ? [data.photo_path] : []);
        setPhotoPaths(paths);
      } catch {
        toast.error("Адуу олдсонгүй");
        navigate("/admin");
      }
    })();
  }, [id, isEdit, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Адууны нэр заавал шаардлагатай");
      return;
    }
    setSubmitting(true);
    const payload = {
      ...form,
      birth_year: form.birth_year ? Number(form.birth_year) : null,
      father_id: form.father_id || null,
      mother_id: form.mother_id || null,
    };
    Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
    try {
      if (isEdit) {
        await api.put(`/horses/${id}`, payload);
        toast.success("Амжилттай шинэчиллээ");
        navigate(`/horse/${id}`);
      } else {
        const { data } = await api.post(`/horses`, payload);
        toast.success("Амжилттай бүртгэгдлээ");
        navigate(`/horse/${data.id}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Хадгалах үед алдаа гарлаа");
    } finally {
      setSubmitting(false);
    }
  };

  const onPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // allow re-selecting same file later
    if (!file) return;
    if (!isEdit) {
      toast.info("Эхлээд адууг хадгалаад дараа нь зураг оруулна уу");
      return;
    }
    if (photoPaths.length >= 2) {
      toast.error("Адуунд хамгийн ихдээ 2 зураг оруулна");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/horses/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPaths(data.photo_paths || []);
      toast.success("Зураг амжилттай орлоо");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Зураг оруулахад алдаа гарлаа");
    } finally {
      setUploading(false);
    }
  };

  const onPhotoDelete = async (idx) => {
    if (!window.confirm("Зургийг устгах уу?")) return;
    try {
      const { data } = await api.delete(`/horses/${id}/photo/${idx}`);
      setPhotoPaths(data.photo_paths || []);
      toast.success("Зураг устгагдлаа");
    } catch {
      toast.error("Устгах үед алдаа гарлаа");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/admin" className="inline-flex items-center gap-2 text-[#1B3B5A] hover:text-[#B8860B] mb-6" data-testid="form-back">
        <ArrowLeft size={18} strokeWidth={1.5} /> Буцах
      </Link>
      <div className="text-xs uppercase tracking-[0.3em] text-[#B8860B] mb-2 font-bold">
        {isEdit ? "Адуу засах" : "Шинэ адуу бүртгэх"}
      </div>
      <h1 className="text-3xl font-bold text-[#1B3B5A] mb-8">
        {isEdit ? form.name || "Засварлах" : "Адуу бүртгэл"}
      </h1>

      <form onSubmit={onSubmit} className="space-y-6" data-testid="horse-form">
        <Section title="Үндсэн мэдээлэл">
          <Field label="Нэр (дуудлага) *">
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="field-name" />
          </Field>
          <Field label="Төрсөн он">
            <input type="number" value={form.birth_year} onChange={(e) => set("birth_year", e.target.value)} className={inputCls} min="1900" max="2100" data-testid="field-year" />
          </Field>
          <Field label="Зүс">
            <select value={form.color} onChange={(e) => set("color", e.target.value)} className={inputCls} data-testid="field-color">
              <option value="">— Сонгох —</option>
              {COLOR_OPTIONS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Хүйс">
            <select value={form.gender} onChange={(e) => set("gender", e.target.value)} className={inputCls} data-testid="field-gender">
              <option value="">— Сонгох —</option>
              {GENDER_OPTIONS.map((g) => <option key={g}>{g}</option>)}
            </select>
          </Field>
          <Field label="Микрочип">
            <input value={form.microchip} onChange={(e) => set("microchip", e.target.value)} className={inputCls} data-testid="field-microchip" />
          </Field>
          <Field label="Тамга (текстээр)">
            <input value={form.brand_stamp} onChange={(e) => set("brand_stamp", e.target.value)} className={inputCls} data-testid="field-brand" placeholder="Жишээ: Б-1" />
          </Field>
          <Field label="Бүртгэлийн дугаар">
            <input value={form.registration_number} onChange={(e) => set("registration_number", e.target.value)} className={inputCls} data-testid="field-reg" />
          </Field>
          <Field label="Улс">
            <input value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls} data-testid="field-country" placeholder="Монгол" />
          </Field>
        </Section>

        <Section title="Угшил (Эцэг, Эх)">
          <ParentSelector
            label="Эцэг адуу"
            value={form.father_id}
            selectedName={fatherName}
            excludeId={id}
            onChange={(pid, pname) => { set("father_id", pid); setFatherName(pname || ""); }}
            testId="parent-father"
          />
          <ParentSelector
            label="Эх адуу"
            value={form.mother_id}
            selectedName={motherName}
            excludeId={id}
            onChange={(pid, pname) => { set("mother_id", pid); setMotherName(pname || ""); }}
            testId="parent-mother"
          />
        </Section>

        <Section title="Эзэмшил">
          <Field label="Эзэмшигч">
            <input value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)} className={inputCls} data-testid="field-owner" />
          </Field>
          <Field label="Үржүүлэгч (Адууны аж ахуй)">
            <input value={form.breeder_farm} onChange={(e) => set("breeder_farm", e.target.value)} className={inputCls} data-testid="field-breeder" />
          </Field>
        </Section>

        <Section title="Нэмэлт">
          <Field label="Амжилт" full>
            <textarea value={form.achievements} onChange={(e) => set("achievements", e.target.value)} rows={3} className={inputCls} data-testid="field-achievements" placeholder="Жишээ: 2024 Наадам айрагдсан..." />
          </Field>
          <Field label="Нэмэлт мэдээлэл" full>
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className={inputCls} data-testid="field-description" />
          </Field>
        </Section>

        <Section title="Зар">
          <Field label="Зарагдаж байна уу?" full>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={form.is_for_sale} onChange={(e) => set("is_for_sale", e.target.checked)} data-testid="field-for-sale" />
              <span className="text-sm">Энэ адууг зарах (ticker-т харагдана)</span>
            </label>
          </Field>
          {form.is_for_sale && (
            <>
              <Field label="Үнэ (заавал биш)">
                <input value={form.sale_price} onChange={(e) => set("sale_price", e.target.value)} className={inputCls} data-testid="field-sale-price" placeholder="жишээ: 15 сая ₮" />
              </Field>
              <Field label="Холбоо барих">
                <input value={form.sale_contact} onChange={(e) => set("sale_contact", e.target.value)} className={inputCls} data-testid="field-sale-contact" placeholder="99xxxxxx" />
              </Field>
            </>
          )}
        </Section>

        {isEdit && (
          <Section title="Зураг (хамгийн ихдээ 2)">
            <div className="md:col-span-2">
              <div className="flex flex-wrap items-start gap-4">
                {photoPaths.map((p, idx) => (
                  <div key={p + idx} className="relative" data-testid={`photo-slot-${idx}`}>
                    <img src={fileUrl(p)} alt={`horse-${idx + 1}`} className="w-32 h-32 object-cover rounded border border-[#D6CFC4]" />
                    <button
                      type="button"
                      onClick={() => onPhotoDelete(idx)}
                      className="absolute -top-2 -right-2 bg-[#8B2500] text-white rounded-full p-1 hover:bg-[#6F1D00]"
                      title="Зургийг устгах"
                      data-testid={`photo-delete-${idx}`}
                    >
                      <Trash2 size={12} strokeWidth={2} />
                    </button>
                  </div>
                ))}
                {photoPaths.length < 2 && (
                  <label className="inline-flex items-center gap-2 bg-[#B8860B] text-white hover:bg-[#996F09] px-4 py-2 rounded cursor-pointer transition-colors text-sm font-bold h-fit self-center">
                    <Upload size={16} strokeWidth={1.5} />
                    {uploading ? "Оруулж байна..." : photoPaths.length === 0 ? "Зураг оруулах" : "2-р зураг нэмэх"}
                    <input type="file" accept="image/*" onChange={onPhotoUpload} className="hidden" data-testid="field-photo" />
                  </label>
                )}
              </div>
              <div className="text-xs text-[#5C4F42] mt-3">{photoPaths.length}/2 зураг ачаалагдсан</div>
            </div>
          </Section>
        )}

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#D6CFC4]">
          <Link to="/admin" className="px-5 py-2.5 text-[#5C4F42] hover:text-[#2C241B]">Цуцлах</Link>
          <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 bg-[#1B3B5A] text-white hover:bg-[#264F78] px-6 py-3 rounded font-bold transition-colors disabled:opacity-60" data-testid="form-submit">
            <Save size={16} strokeWidth={1.5} />
            {submitting ? "Хадгалж байна..." : isEdit ? "Шинэчлэх" : "Хадгалах"}
          </button>
        </div>
      </form>
    </div>
  );
};

const inputCls = "w-full border border-[#D6CFC4] rounded px-3 py-2 focus:outline-none focus:border-[#B8860B] transition-colors";
const Section = ({ title, children }) => (
  <div className="bg-white border border-[#D6CFC4] rounded-lg p-5 md:p-6">
    <h3 className="text-lg font-bold text-[#1B3B5A] mb-4">{title}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
  </div>
);
const Field = ({ label, children, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="text-sm font-bold text-[#5C4F42] mb-1 block">{label}</label>
    {children}
  </div>
);

export default HorseForm;
